import React, { useEffect, useRef, useState } from "react";
import { Grid, SelectChangeEvent, Typography, Box } from "@mui/material";
import {
  PortfolioFilterPages,
  ProjectRegisterMetrics,
} from "constants/constant";

import { metrics } from "pages/Dashboards/ProjectRegister/data/ProjectRegisterData";

import ProjectMetrics from "pages/Dashboards/ProjectRegister/ProjectMetrics";
import { useAppState } from "context/appState.context";
import { _getLocations } from "middlewares/LocationApi/location";
import { ProjectRegisterFilter } from "../Data/ProjectRegisterFilter";
import {
  _addPortfolioFilterSelection,
  _getMultipleLocationProjectRegisterDetails,
  _getPortfolioFilterSelection,
} from "middlewares/DashboardApi/portfolioView";
import { ProjectRegisterModel } from "../Data/ProjectRegisterModel";
import ProjectRegisterTable from "./ProjectRegisterTable";
import Spinner from "components/Spinner";
import { ProjectRegisterResult, WaterfallGroupModel } from "../Data/ProjectRegisterResult";
import MarginalAbatementCostCurve from "pages/Dashboards/ProjectRegister/MarginalAbatementCostCurve";
import { MACgraphModel } from "pages/Dashboards/ProjectRegister/data/MACgraphModel";
import { TLFMetricsData } from "pages/Dashboards/ProjectRegister/data/TLFMetricsData";
import { generateLocationLevelData } from "pages/Dashboards/ProjectRegister/data/metricsUtils";
import TimelineAndFinancialMetrics from "pages/Dashboards/ProjectRegister/TimelineAndFinancialMetrics";
import ProjectRegisterDropdown from "../ProjectRegisterDropdown";
import { PortfolioFilterSelection } from "../Data/PortfolioFilterSelection";
import FilterSection from "./FilterSection";
import MeasureImpactsbyScope from "pages/Dashboards/ProjectRegister/MeasureImpactsbyScope";
import { MIBScopeData } from "pages/Dashboards/ProjectRegister/data/MIBScopeData";
import { FilterParam } from "../Data/FilterParam";

const buildWaterfallChartData = (res: ProjectRegisterResult): MIBScopeData[] => {
  const waterfallGroups = res.waterfallGroups ?? [];
  const baseline = res.waterfallBaseline;

  if (!baseline || waterfallGroups.length === 0) {
    return [];
  }

  const chartData: MIBScopeData[] = [];

  // Start bar: BAU Emissions
  chartData.push({
    label: "BAU Emissions",
    value: baseline.bauEmissions ?? 0,
    barType: "start",
    color: "#A9A9A9",
    measures: [],
  });

  // Historical Emissions bar (reduction from BAU)
  if (baseline.historicalEmissions != null) {
    chartData.push({
      label: "Historical Emissions",
      value: baseline.historicalEmissions,
      barType: "historical",
      color: "#5e4bea",
      measures: [],
    });
  }

  // Growth bar (addition on top of Historical Emissions)
  if (baseline.growthEmissions != null) {
    chartData.push({
      label: "Growth",
      value: baseline.growthEmissions,
      barType: "growth",
      color: "#A020F0",
      measures: [],
    });
  }

  // Measure groups
  waterfallGroups.forEach((group: WaterfallGroupModel) => {
    chartData.push({
      label: group.groupLabel ?? "Unknown",
      value: group.emissionReduction ?? 0,
      barType: "reduction",
      measures: group.measures ?? [],
      organizations: group.organizations ?? [],
    });
  });

  chartData.push({
    label: "Residual emissions",
    value: baseline.residualEmissions ?? 0,
    barType: "end",
    color: "#A9A9A9",
    measures: [],
    scopeByEmissions: baseline.scopeByEmissions ?? [],
  });

  return chartData;
};

const PortfolioProjectRegister = () => {
  const { currency } = useAppState();
  const SCALE_FACTOR = 0.75;
  const scaledWidth = `${100 / SCALE_FACTOR}%`;

  const rootLayoutSx = {
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    position: "relative",
  };

  const scaledLayoutSx = {
    transform: `scale(${SCALE_FACTOR})`,
    transformOrigin: "top left",
    width: scaledWidth,
    minHeight: "100vh",
  };

  const sectionItemSx = {
    width: "100%",
    maxWidth: "100%",
    px: 0,
  };

  const sectionContainerSx = {
    width: "100%",
    maxWidth: "100%",
  };

  const [selectedOption, setSelectedOption] = useState(
    ProjectRegisterMetrics.MarginalAbatementCostCurve
  );
  const [loading, setLoading] = useState(false);

  const [locationOptions, setLocationOptions] = useState<
    { id: number; label: string; value: string }[]
  >([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([
    -1,
  ]);
  const [metricsData, setMetricsData] = useState(metrics);
  const [projectRegisterFilter, setProjectRegisterFilter] =
    React.useState<ProjectRegisterFilter>({
      locationId: [],
      baseYear: [],
      dashboardSection: selectedOption,
    });

  const [projectRegisterModelList, setProjectRegisterModelList] = useState<
    Array<ProjectRegisterModel>
  >([]);
  const [mibScopeData, setMibScopeData] = useState<MIBScopeData[]>([]);
  const [isMacDataLoaded, setIsMacDataLoaded] = useState(false);
  const [macData, setMacData] = useState<MACgraphModel[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [param, setParam] = useState<FilterParam[]>([]);
  const isInitialRender = useRef(true);
  const [tlfMetricsData, setTlfMetricsData] = useState<TLFMetricsData[]>([]);

  const handleOptionChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    setSelectedOption(selectedValue);
    setProjectRegisterFilter((prevFilter) => ({
      ...prevFilter,
      dashboardSection: selectedValue, // Update the dashboardSection with the selected option
    }));
  };

  const handleLocationChange = (selectedOptions: number[]) => {
    setSelectedLocations(selectedOptions);
  };

  function getEssentialLocationDetails() {
    _getLocations()
      .then((res) => {
        if (res) {
          const formattedOptions = res.map((loc) => ({
            id: loc.locationId,
            label: loc.locationName,
            value: loc.locationId.toString(),
          }));
          const selectAllOption = {
            id: 0,
            label: "Select All",
            value: "0",
          };
          setLocationOptions([selectAllOption, ...formattedOptions]);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
        setLoading(false);
      });
  }

  async function getMultipleLocationProjectRegisterDetails() {
    setLoading(true);
    try {
      const filter = { ...projectRegisterFilter };
      // Set groupBy for waterfall structure when MeasureImpactsbyScope is selected
      if (selectedOption === ProjectRegisterMetrics.MeasureImpactsbyScope) {
        filter.groupBy = "location";
      }
      const res = await _getMultipleLocationProjectRegisterDetails(filter);
      handleResponse(res);
    } catch (err) {
      handleError(err);
    }
  }

  function handleResponse(res: any) {
    if (res) {
      const { projectRegisterModelList, dashboardMetric } = res;
      updateMetricsData(dashboardMetric);
      updateProjectRegisterModelList(projectRegisterModelList);

      if (isInitialRender.current) {
        
        const param: FilterParam[] = projectRegisterModelList.map(
          (item: any) => ({
            locationId: item.locationId,
            scenarioSection: item.scenarios.length > 0 ? item.scenarios[0] : "",
          })
        );
        setParam(param);
        isInitialRender.current = false; // Mark as not the first render anymore
      }
      if (
        selectedOption === ProjectRegisterMetrics.MarginalAbatementCostCurve
      ) {
        setMacData(res.locationLevelMACGraphModelList);
        setIsMacDataLoaded(true);
      }
      if (
        selectedOption == ProjectRegisterMetrics.TimelineandFinancialMetrics
      ) {
        const data = generateLocationLevelData(res);
        setTlfMetricsData(data);
        setIsDataLoaded(true);
      }
      if (selectedOption === ProjectRegisterMetrics.MeasureImpactsbyScope) {
        // Use new waterfallGroups API structure if available (production)
        if (res.waterfallGroups && res.waterfallBaseline) {
          setMibScopeData(buildWaterfallChartData(res));
        } else if (res.impactByScopeModelList) {
          // Fallback to old structure for backward compatibility
          const data: MIBScopeData[] = res.impactByScopeModelList.map(
            (item: any, index: any) => {
              const mibScopeData: MIBScopeData = {
                label: item.locationName,
                value: item.emissionReduction,
                scopeByEmissions: item.scopeByEmissions ?? [],
              };
              return mibScopeData;
            }
          );
          setMibScopeData(data);
        } else {
          setMibScopeData([]);
        }
      }
    }
    setLoading(false);
  }

  function handleError(err: any) {
    console.error("Error:", err);
    setLoading(false);
  }

  function updateMetricsData(dashboardMetric: any) {
    setMetricsData((prevMetricsData) =>
      prevMetricsData.map((metric) => ({
        ...metric,
        value: dashboardMetric[metric.key],
      }))
    );
  }

  function updateProjectRegisterModelList(projectRegisterModelList: any[]) {
    const modifiedRes = projectRegisterModelList.map((record, index) => ({
      ...record,
      rowId: `${index + 1}`,
    }));
    setProjectRegisterModelList(modifiedRes);
  }

  const handleSearch = async () => {
    const filteredParam = param.filter((item) =>
      selectedLocations.includes(Number(item.locationId))
    );
    setProjectRegisterFilter((prevFilter) => ({
      ...prevFilter,
      locationId: selectedLocations,
      param: filteredParam,
    }));
    savePortfolioFilterSelection();
  };

  const getPortfolioFilterSelection = async (): Promise<void> => {
    try {
      const res = await _getPortfolioFilterSelection(
        PortfolioFilterPages.ProjectRegister
      );
      if (res) {
        updateProjectRegisterFilterFromResponse(res);
      }
    } catch (err) {
      console.error("Error:", err);
      setLoading(false);
    }
  };

  const savePortfolioFilterSelection = async () => {
    const param: FilterParam[] = projectRegisterModelList.map((item: any) => ({
      locationId: item.locationId,
      scenarioSection: item.scenarios.length > 0 ? item.scenarios[0] : "",
    }));

    const portfolioFilterSelection: PortfolioFilterSelection = {
      userEmail: null,
      pageName: PortfolioFilterPages.ProjectRegister,
      locationIds: selectedLocations.join(","),
      baseYear: null,
      scopes: null,
      scenarioSection: param
        .filter((item) => selectedLocations.includes(item.locationId))
        .map((item) => item.scenarioSection)
        .join(","),
    };

    try {
      const res = await _addPortfolioFilterSelection(portfolioFilterSelection);
      if (res) {
        updateProjectRegisterFilterFromResponse(res);
      }
    } catch (err) {
      console.error("Error:", err);
      setLoading(false);
    }
  };

  const updateProjectRegisterFilterFromResponse = (res: any) => {
    const { locationIds, scenarioSection } = res;
  
    // Parse locationIds and scenarioSection into arrays
    const locationIdsArray = locationIds
      ? locationIds
        .split(",")
        .map((id: string) => parseInt(id, 10))
        .filter((id:any) => !isNaN(id))
      : [];
    const scenarioSectionsArray = (scenarioSection ?? "").split(",");
  
    // Create param array by combining locationIdsArray and scenarioSectionsArray
    const param: FilterParam[] = locationIdsArray.map((locationId:number, index:number) => ({
      locationId,
      scenarioSection: scenarioSectionsArray[index] ?? "Scenario A",
    }));

    // Update project register filter and selected locations
    setProjectRegisterFilter((prevFilter) => ({
      ...prevFilter,
      param,
    }));
  
    setSelectedLocations(locationIdsArray);
  };
  

  useEffect(() => {
    if (
      selectedLocations.length > 0 &&
      projectRegisterFilter.locationId.length > 0
    ) {
      getMultipleLocationProjectRegisterDetails();
    }
  }, [projectRegisterFilter.locationId, selectedOption]);

  useEffect(() => {
    getEssentialLocationDetails();
    getPortfolioFilterSelection();
  }, []);

  return (
    <Box sx={rootLayoutSx}>
      <Box sx={scaledLayoutSx}>
      <Grid container spacing={0} sx={{ ...sectionContainerSx, mt: 0 }}>
      <Grid item xs={12} sx={sectionItemSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                color: "#0C0808",
                marginLeft: "6px",
              }}
              data-testid="marginalAbatementCostCurveLabel"
            >
              {selectedOption}
            </Typography>
          </Box>
          <Box>
            <ProjectRegisterDropdown
              selectedOption={selectedOption}
              handleOptionChange={handleOptionChange}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sx={sectionItemSx}>
        <FilterSection
          locationOptions={locationOptions}
          selectedLocations={selectedLocations}
          onLocationChange={handleLocationChange}
          onSearch={handleSearch}
        />
      </Grid>
      {loading ? (
        <Spinner size={80} data-testid="spinner" />
      ) : (
        <>
          <Grid item xs={12} sx={sectionItemSx}>
            <Grid container spacing={0} sx={sectionContainerSx}>
              <ProjectMetrics metrics={metricsData} isPortfolioView={true} currency={currency}></ProjectMetrics>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={sectionItemSx}>
            <Grid container spacing={0} sx={sectionContainerSx}>
              {selectedOption ===
                ProjectRegisterMetrics.MarginalAbatementCostCurve &&
                isMacDataLoaded && (
                  <MarginalAbatementCostCurve data={macData} labelKey="locationName" currency={currency} />
                )}
              {selectedOption ===
                ProjectRegisterMetrics.TimelineandFinancialMetrics &&
                isDataLoaded && (
                  <TimelineAndFinancialMetrics
                    data={tlfMetricsData}
                    width={1100}
                    height={450}
                  />
                )}
              {selectedOption ===
                ProjectRegisterMetrics.MeasureImpactsbyScope && (
                <MeasureImpactsbyScope data={mibScopeData} currency={currency} />
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} sx={sectionItemSx}>
            <Grid container spacing={0} sx={sectionContainerSx}>
              <ProjectRegisterTable
                projectRegisterModel={projectRegisterModelList}
                currency={currency}
              ></ProjectRegisterTable>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
      </Box>
    </Box>
  );
};

export default PortfolioProjectRegister;
